import type { ParseKeys } from "i18next"
import { TeamManagementPicIcon, ManageMembersIcon } from "../../../../assets/customer-settings-icons"
import { useDeleteUserTeamMemberMutation, useLazyGetAllTeamMembersForCustomerQuery } from "../../../../service/team-management-service"
import { UserTeamMemberResponse } from "../../../../service/contract/team-management"
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout"
import styles from "./team-management-page.module.scss"
import { useCallback, useEffect, useState } from "react"
import { CustomerLoader, ConfirmationModal } from "../../components"
import { useNavigate } from "react-router"
import { ApplicationRoutes } from "../../../../routes"
import { CustomerType } from "../../../../model/customer/customer-type"
import { useSelector } from "react-redux"
import { RootState } from "../../../../stores/store"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { InviteMemberModal } from "./components/invite-member-modal/InviteMemberModal"
import Button, { ButtonRadius, ButtonSize, ButtonStyle } from "../../../../common/components/button/button"
import Can from "../../../../guards/Can"
import { CustomerPermissions } from "../../../../model/permissions/permissions"

const BENEFIT_ITEMS: { icon: JSX.Element; titleKey: ParseKeys; descriptionKey: ParseKeys }[] = [
  {
    titleKey: "corporate.sharedAccountOnboardingShareResponsibilityTitle",
    descriptionKey: "corporate.sharedAccountOnboardingShareResponsibilityDescription",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="8.5" cy="7" r="3" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="16" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M2.5 19c.6-3 3-5 6-5s5.4 2 6 5M14.5 14.4c2.4.3 4.3 1.9 4.9 4.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    titleKey: "corporate.sharedAccountOnboardingSharedVisibilityTitle",
    descriptionKey: "corporate.sharedAccountOnboardingSharedVisibilityDescription",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ),
  },
  {
    titleKey: "corporate.sharedAccountOnboardingSharePaymentsTitle",
    descriptionKey: "corporate.sharedAccountOnboardingSharePaymentsDescription",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="10" cy="7.5" r="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M4 19c.6-3 3-5 6-5 1.2 0 2.3.3 3.2.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="m14.5 17 2 2 3.5-3.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    titleKey: "corporate.sharedAccountOnboardingStayInControlTitle",
    descriptionKey: "corporate.sharedAccountOnboardingStayInControlDescription",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M4 8.5h9M17 8.5h3M4 15.5h3M11 15.5h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="15" cy="8.5" r="2" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="9" cy="15.5" r="2" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ),
  },
];

export const TeamManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userInfo = useSelector((state: RootState) => state.user);

  const [getAllTeamMembersForCustomer, { isLoading: isLoadingTeamMembers, isFetching: isFetchingTeamMembers }] = useLazyGetAllTeamMembersForCustomerQuery();
  const [teamMembers, setTeamMembers] = useState<UserTeamMemberResponse[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [introAcknowledged, setIntroAcknowledged] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const result = await getAllTeamMembersForCustomer().unwrap();
      setTeamMembers(result);
    } catch (error: any) {
      // toast.error(error?.data?.title || "Failed to fetch team members.");
    } finally {
      setHasFetched(true);
    }
  }, [getAllTeamMembersForCustomer]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const handleInviteModalClose = (invited?: boolean) => {
    setIsInviteModalOpen(false);
    if (invited) fetchTeamMembers();
  };

  const [openMenuMemberId, setOpenMenuMemberId] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<UserTeamMemberResponse | null>(null);
  const [deleteTeamMember, { isLoading: isRemoving }] = useDeleteUserTeamMemberMutation();

  // Resend/revoke endpoints are not on the backend yet — surface the design's
  // actions with an informational notice until they land.
  const handleResendInvitation = (_member: UserTeamMemberResponse) => {
    setOpenMenuMemberId(null);
    toast.info(t("account.web.resendInvitationComingSoon"));
  };

  const handleRevokeInvitation = (_member: UserTeamMemberResponse) => {
    setOpenMenuMemberId(null);
    toast.info(t("account.web.revokeInvitationComingSoon"));
  };

  const handleChangeRole = () => {
    setOpenMenuMemberId(null);
    navigate(`./${ApplicationRoutes.MANAGE_TEAM_MEMBERS}`);
  };

  const handleRemoveMemberClick = (member: UserTeamMemberResponse) => {
    setOpenMenuMemberId(null);
    setMemberToRemove(member);
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove) return;
    try {
      await deleteTeamMember({ userReferenceId: memberToRemove.id }).unwrap();
      toast.success(t("account.web.memberRemoved"));
      setMemberToRemove(null);
      fetchTeamMembers();
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.unableToRemoveMember"));
    }
  };

  const isIndividual = userInfo.customerType === CustomerType.Individual;

  if (isLoadingTeamMembers || isFetchingTeamMembers || !hasFetched) {
    return (
      <CustomerPageLayout title={isIndividual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
        <CustomerLoader
          title={isIndividual ? t("account.web.loadingFamilyAccountManagement") : t("account.web.loadingCorporateAccountManagement")}
          subtitle={t("account.web.pleaseWaitFetchingInformation")}
        />
      </CustomerPageLayout>
    );
  }

  // Landing/intro: first visit with no members yet (Figma: Shared Account Management — Landing)
  if (teamMembers.length === 0 && !introAcknowledged) {
    return (
      <CustomerPageLayout title={t("account.web.accountManagement")}>
        <div className={styles.intro}>
          <div className={styles.introIllustration}>
            <TeamManagementPicIcon />
          </div>

          <div className={styles.introHeading}>
            <h2 className={styles.introTitle}>{isIndividual ? t("corporate.sharedAccountOnboardingFamilyTitle") : t("corporate.sharedAccountOnboardingCorporateTitle")}</h2>
            <p className={styles.introSubtitle}>
              {t("corporate.sharedAccountOnboardingDescription")}
            </p>
          </div>

          <div className={styles.introBenefits}>
            {BENEFIT_ITEMS.map(item => (
              <div key={item.titleKey} className={styles.introBenefitRow}>
                <span className={styles.introBenefitIcon}>{item.icon}</span>
                <div className={styles.introBenefitText}>
                  <span className={styles.introBenefitTitle}>{t(item.titleKey)}</span>
                  <span className={styles.introBenefitDescription}>{t(item.descriptionKey)}</span>
                </div>
              </div>
            ))}
          </div>

          <button type="button" className={styles.introContinueButton} onClick={() => setIntroAcknowledged(true)}>
            {t("common.actions.continueKey")}
          </button>
        </div>
      </CustomerPageLayout>
    );
  }

  // Members view (Figma: Shared Account Members)
  return (
    <CustomerPageLayout title= {teamMembers.length > 0 ? (isIndividual ? t("corporate.sharedAccountOnboardingFamilyTitle") : t("corporate.sharedAccountOnboardingCorporateTitle")) : t("corporate.sharedAccountMembersTitle")}
 showBackButton subtitle={t("account.web.inviteTrustedMembersShort")}>
      <div className={styles.membersPage}>
       


        <div className={styles.membersToolbar}>
          <div className={styles.membersToolbarText}>
            <span className={styles.membersToolbarTitle}>{t("account.web.invitedMembers")}</span>
            <span className={styles.membersToolbarCount}>
              {teamMembers.length === 1 ? t("account.web.memberInvitedCount", { count: teamMembers.length }) : t("account.web.membersInvitedCount", { count: teamMembers.length })}
            </span>
          </div>
          <div className={styles.membersToolbarActions}>
            {teamMembers.length > 0 && (
              <Button
                type="button"
                style={ButtonStyle.WHITE}
                radius={ButtonRadius.PILL}
                size={ButtonSize.SM}
                leftIcon={<ManageMembersIcon height={15} width={18} />}
                onClick={() => navigate(`./${ApplicationRoutes.MANAGE_TEAM_MEMBERS}`)}
              
                title={t("common.labels.manageTeamMembers")}
                />
            )}
            <Can permission={CustomerPermissions.AddNewTeamMember}>
              <button
                type="button"
                className={styles.membersPillButton}
                onClick={() => setIsInviteModalOpen(true)}
              >
                {t("corporate.inviteMember")}
                <span className={styles.membersPillPlus}>+</span>
              </button>
            </Can>
          </div>
        </div>

        <div className={styles.membersCardArea}>
          {teamMembers.length === 0 ? (
            <div className={styles.membersEmptyState}>
              <span className={styles.membersEmptyIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="8.5" cy="7" r="2.8" stroke="currentColor" strokeWidth="1.5" />
                  <circle cx="16" cy="7.8" r="2.2" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M2.8 19c.6-2.9 2.9-4.8 5.7-4.8 2.9 0 5.2 1.9 5.8 4.8M14.8 14.6c2.2.3 4 1.8 4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>
              <span className={styles.membersEmptyText}>{t("corporate.sharedAccountInvitedMembersEmptyTitle")}</span>
            </div>
          ) : (
            <div className={styles.memberRows}>
              {teamMembers.map((member) => {
                const fullName = `${member.firstName} ${member.lastName}`.trim();
                const initials = `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
                const isMenuOpen = openMenuMemberId === member.id;

                return (
                  <div key={member.id} className={styles.memberRow}>
                    <div className={styles.memberRowAvatar}>
                      {member.image ? <img src={member.image} alt={fullName} /> : initials}
                    </div>
                    <div className={styles.memberRowInfo}>
                      <span className={styles.memberRowName}>{fullName}</span>
                      <span className={styles.memberRowMeta}>
                        <span className={`${styles.memberStatusBadge} ${member.isActive ? styles.memberStatusActive : styles.memberStatusPending}`}>
                          <span className={styles.memberStatusDot} />
                          {member.isActive ? t("corporate.activeTeamMember") : t("corporate.pendingTeamMember")}
                        </span>
                        <span className={styles.memberMetaSeparator} />
                        <span className={styles.memberRowRole}>{member.role.name}</span>
                      </span>
                    </div>
                    <div className={styles.memberMenuAnchor}>
                      <button
                        type="button"
                        className={styles.memberKebabButton}
                        onClick={() => setOpenMenuMemberId(isMenuOpen ? null : member.id)}
                        aria-label={t("account.web.actionsForMember", { name: fullName })}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                          <circle cx="3" cy="8" r="1.2" fill="currentColor" />
                          <circle cx="8" cy="8" r="1.2" fill="currentColor" />
                          <circle cx="13" cy="8" r="1.2" fill="currentColor" />
                        </svg>
                      </button>

                      {isMenuOpen && (
                        <div className={styles.memberMenu}>
                          {!member.isActive && (
                            <>
                              <button type="button" className={styles.memberMenuItem} onClick={() => handleResendInvitation(member)}>
                                <span className={styles.memberMenuIcon}>
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <path d="M4.5 9a8 8 0 1 1-.7 5.5M4.5 4v5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </span>
                                {t("common.labels.sharedAccountMenuResendInvitationTitle")}
                              </button>
                              <button type="button" className={styles.memberMenuItem} onClick={() => handleRevokeInvitation(member)}>
                                <span className={styles.memberMenuIcon}>
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                                    <path d="M5.8 5.8l12.4 12.4" stroke="currentColor" strokeWidth="1.5" />
                                  </svg>
                                </span>
                                {t("common.labels.sharedAccountMenuRevokeInvitationTitle")}
                              </button>
                            </>
                          )}
                          {member.isActive && (
                            <button type="button" className={styles.memberMenuItem} onClick={() => handleChangeRole()}>
                              <span className={styles.memberMenuIcon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                                  <path d="M14.5 5.5 18.5 9.5 8 20H4v-4L14.5 5.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                </svg>
                              </span>
                              {t("common.labels.sharedAccountMenuChangeRoleTitle")}
                            </button>
                          )}
                          <button type="button" className={styles.memberMenuItem} onClick={() => handleRemoveMemberClick(member)}>
                            <span className={styles.memberMenuIcon}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M5.5 7h13M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7m3 0v11.5A1.5 1.5 0 0 1 16 20H8a1.5 1.5 0 0 1-1.5-1.5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                            {t("common.labels.sharedAccountMenuRemoveMemberTitle")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {openMenuMemberId && <div className={styles.memberMenuBackdrop} onClick={() => setOpenMenuMemberId(null)} />}

        {isInviteModalOpen && <InviteMemberModal onClose={handleInviteModalClose} />}

        <ConfirmationModal
          isOpen={!!memberToRemove}
          onClose={() => setMemberToRemove(null)}
          onConfirm={handleConfirmRemoveMember}
          title={t("account.web.removeMemberConfirmTitle", { name: memberToRemove ? `${memberToRemove.firstName} ${memberToRemove.lastName}`.trim() : t("account.web.memberLabel") })}
          message={t("account.web.memberWillLoseAccess")}
          confirmText={t("account.web.remove")}
          loadingText={t("account.web.removing")}
          isLoading={isRemoving}
          variant="danger"
        />
      </div>
    </CustomerPageLayout>
  )
}
